import * as React from "react";
import styled from "styled-components";
import loadable from "@loadable/component";
import { InView } from "react-intersection-observer";
/// component
import VideoPlayMark from "common/icons/svgs/video-play-mark.svg";
import IconTouchArea from "common/components/iconTouchArea";
import ImageComponent from "common/components/imageHolder";
import MediaLazyTransitionWrapper from "common/components/mediaWrapper";

const VideoLibrary = loadable.lib(
  /* webpackChunkName: "[request]" */ () => import("video.js"),
);

const VIDEO_OPTION = {
  controls: true,
  autoplay: true,
  loop: false,
  preload: "auto",
  fluid: true,
  html5: {
    nativeControlsForTouch: true,
    nativeAudioTracks: false,
    nativeTextTracks: false,
    nativeVideoTracks: false,
    hls: {
      overrideNative: true,
    },
  },
};

const BlockResourceWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  margin: auto;

  img {
    display: inline-block;
    vertical-align: top;
    width: 100%;
    margin: auto;
  }

  video {
    display: block;
    width: 100%;
    height: 100%;
    max-width: 100%;
  }
`;

interface IProps {
  block: Vingle.IVideo;
  paused?: boolean;
}

class HlsVideoPlayer extends React.PureComponent<IProps> {
  private refVideoElement = React.createRef<HTMLVideoElement>();
  private videojs: any = null;
  private player: any = null;

  public async componentDidUpdate(prevProps: IProps) {
    const { paused } = this.props;
    const videoElement = this.refVideoElement.current;
    if (
      this.player &&
      videoElement &&
      prevProps.paused !== paused &&
      videoElement.muted &&
      paused === !videoElement.paused
    ) {
      const playPromise = videoElement.play();
      if (playPromise) {
        await playPromise;
      }
      if (paused) {
        videoElement.pause();
      }
    }
  }

  public componentWillUnmount() {
    if (this.player) {
      this.player.dispose();
    }
  }

  public render() {
    const block = this.props.block;
    const { width, height } = block;
    const mp4Source = this.getMp4Source();

    return (
      <>
        <VideoLibrary ref={this.handleVideoLibrary} />
        {mp4Source && width && height ? (
          <MediaLazyTransitionWrapper
            src={mp4Source.src}
            type="video"
            width={width}
            height={height}
            previewColor={block["preview-color"]}
            blurHash={block.blur_hash}
            onSuccess={this.handleVideoSuccess}
          >
            {this.renderVideo()}
          </MediaLazyTransitionWrapper>
        ) : (
          <div>{this.renderVideo()}</div>
        )}
      </>
    );
  }

  private readonly handleVideoLibrary = ({ default: videojs }: any) => {
    this.videojs = videojs;
    if (this.refVideoElement.current && videojs) {
      this.handleVideoSuccess();
    }
  };

  private readonly handleVideoSuccess = () => {
    if (!this.player && this.videojs && this.refVideoElement.current) {
      this.player = this.videojs(
        this.refVideoElement.current,
        this.getPlayerOptions(),
      );
    }
  };

  private readonly getSources = () => {
    const { sources } = this.props.block;
    const mp4Source = sources.find(source => source.type === "video/mp4");
    const hlsSource = sources.find(
      source => source.type === "application/x-mpegurl",
    );
    return [hlsSource, mp4Source].filter((v): v is NonNullable<typeof v> =>
      Boolean(v),
    );
  };

  private readonly getMp4Source = () => {
    return this.getSources().find(v => v.type === "video/mp4");
  };

  private readonly renderSourceNodes = () => {
    return this.getSources().map(source => {
      return <source src={source.src} type={source.type} key={source.src} />;
    });
  };

  private readonly renderVideo = () => {
    return (
      <>
        <video
          playsInline={true}
          muted={true}
          autoPlay={true}
          className="video-js vjs-default-skin"
          ref={this.refVideoElement}
        >
          {this.renderSourceNodes()}
        </video>
      </>
    );
  };

  private getPlayerOptions() {
    const block = this.props.block;

    return Object.assign(VIDEO_OPTION, {
      aspectRatio: `${block.width}:${block.height}`,
      poster: block.poster,
    });
  }
}

const VideoPlayer = ({ block }: IProps) => {
  const [isPlaying, setPlayState] = React.useState(false);
  const [isPaused, setPause] = React.useState(false);
  const handlePlay = React.useCallback(() => {
    setPlayState(true);
  }, []);
  const handleVisibilityChange = React.useCallback((visibility: boolean) => {
    setPlayState(true);
    setPause(!visibility);
  }, []);
  let content = null;

  if (isPlaying) {
    content = (
      <BlockResourceWrapper>
        <HlsVideoPlayer block={block} paused={isPaused} />
      </BlockResourceWrapper>
    );
  } else {
    content = (
      <BlockResourceWrapper>
        <ImageComponent
          src={block.poster}
          height={block.height}
          width={block.width}
          previewColor={block["preview-color"]}
          blurHash={block.blur_hash}
          alt="presentation"
        />
        <IconTouchArea onClick={handlePlay}>
          <VideoPlayMark />
        </IconTouchArea>
      </BlockResourceWrapper>
    );
  }

  return <InView onChange={handleVisibilityChange}>{content}</InView>;
};

export default VideoPlayer;
